import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Hook to calculate dynamic max widths for items based on their content.
 * Items that need less space give their excess to items that need more.
 *
 * Performance optimizations:
 * - Caches natural widths (only remeasures when item count changes)
 * - Debounces resize calculations
 * - Only recalculates when container width actually changes
 */
export const useDynamicMaxWidths = (itemCount: number, gap: number) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const itemRefs = useRef<(HTMLDivElement | null)[]>([]);
  const [maxWidths, setMaxWidths] = useState<string[]>(() => Array(itemCount).fill('none'));

  // Cache for natural widths - only needs to be measured once per item set
  const naturalWidthsCache = useRef<number[]>([]);
  const lastContainerWidth = useRef<number>(0);
  const measurementScheduled = useRef<number | null>(null);

  const setItemRef = useCallback(
    (index: number) => (el: HTMLDivElement | null) => {
      itemRefs.current[index] = el;
      // Invalidate cache when refs change
      if (el) naturalWidthsCache.current = [];
    },
    []
  );

  const measureNaturalWidths = useCallback((): number[] => {
    // Return cached values if available
    if (naturalWidthsCache.current.length === itemCount) {
      return naturalWidthsCache.current;
    }

    const items = itemRefs.current.slice(0, itemCount);
    if (items.some(item => !item)) return [];

    // Batch DOM reads: first remove all max-widths
    const originalMaxWidths = items.map(item => item?.style.maxWidth || '');
    items.forEach(item => {
      if (item) item.style.maxWidth = 'none';
    });

    // Force single reflow, then read all widths
    const widths = items.map(item => item?.scrollWidth || 0);

    // Batch DOM writes: restore all max-widths
    items.forEach((item, i) => {
      if (item) item.style.maxWidth = originalMaxWidths[i];
    });

    naturalWidthsCache.current = widths;
    return widths;
  }, [itemCount]);

  const calculateWidths = useCallback(() => {
    if (!containerRef.current || itemCount === 0) return;

    const containerWidth = containerRef.current.offsetWidth;
    if (containerWidth === 0) return;

    // Skip if container width hasn't changed
    if (containerWidth === lastContainerWidth.current && naturalWidthsCache.current.length > 0) {
      return;
    }
    lastContainerWidth.current = containerWidth;

    const naturalWidths = measureNaturalWidths();
    if (naturalWidths.length === 0) return;

    const totalGapWidth = (itemCount - 1) * gap * 8;
    const availableWidth = containerWidth - totalGapWidth;
    const totalNaturalWidth = naturalWidths.reduce((sum, w) => sum + w, 0);

    // If everything fits, no constraints needed
    if (totalNaturalWidth <= availableWidth) {
      setMaxWidths(prev => {
        const allNone = prev.every(w => w === 'none');
        return allNone ? prev : naturalWidths.map(() => 'none');
      });
      return;
    }

    // Calculate fair share and redistribute space
    const fairShare = availableWidth / itemCount;
    let spaceToRedistribute = 0;
    const largeItemIndices: number[] = [];

    naturalWidths.forEach((width, index) => {
      if (width <= fairShare) {
        spaceToRedistribute += fairShare - width;
      } else {
        largeItemIndices.push(index);
      }
    });

    const extraPerLargeItem =
      largeItemIndices.length > 0 ? spaceToRedistribute / largeItemIndices.length : 0;

    const calculatedWidths = naturalWidths.map((naturalWidth, index) => {
      if (naturalWidth <= fairShare) {
        return 'none';
      }
      const maxAllowed = Math.min(naturalWidth, fairShare + extraPerLargeItem);
      return `${Math.floor(maxAllowed)}px`;
    });

    setMaxWidths(prev => {
      // Only update if values actually changed
      const hasChanged = calculatedWidths.some((w, i) => w !== prev[i]);
      return hasChanged ? calculatedWidths : prev;
    });
  }, [itemCount, gap, measureNaturalWidths]);

  const scheduleCalculation = useCallback(() => {
    // Debounce: cancel pending calculation
    if (measurementScheduled.current) {
      cancelAnimationFrame(measurementScheduled.current);
    }
    measurementScheduled.current = requestAnimationFrame(() => {
      calculateWidths();
      measurementScheduled.current = null;
    });
  }, [calculateWidths]);

  useEffect(() => {
    // Reset cache when item count changes
    naturalWidthsCache.current = [];
    lastContainerWidth.current = 0;

    scheduleCalculation();

    const container = containerRef.current;
    if (!container) return;

    const resizeObserver = new ResizeObserver(scheduleCalculation);
    resizeObserver.observe(container);

    return () => {
      if (measurementScheduled.current) {
        cancelAnimationFrame(measurementScheduled.current);
      }
      resizeObserver.disconnect();
    };
  }, [scheduleCalculation, itemCount]);

  return { containerRef, setItemRef, maxWidths };
};
